import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { guidFor } from '@ember/object/internals';
import { sanitizeHtml } from 'beebee-helper/helpers/safe-string';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! Args:\n    @value           — valore corrente (stringa)\n    @onValueChange   — callback(newValue) quando il contenuto cambia\n    @applicationController — opzionale, per il link \"testi predefiniti\"\n    @custom_field    — opzionale, per il link \"testi predefiniti\"\n    @ariaLabel       — opzionale, nome del campo per il box idle (role=textbox)\n}}\n{{#if this.editCode}}\n  <Textarea\n    @value={{this._localValue}}\n    rows=\'4\'\n    {{did-update this.syncValue @value}}\n    {{on \'input\' this.onTextareaInput}}\n  />\n{{else if this.mounted}}\n  <BbEditor\n    @id={{this.elementId}}\n    @value={{@value}}\n    @setValue={{@onValueChange}}\n    @activation={{this.activation}}\n  />\n{{else}}\n  {{! Campo a riposo: deve essere indistinguibile dall\'editor montato, quindi ne ripete\n      la struttura esatta — copia della toolbar vera e .ql-container/.ql-editor come\n      fratelli, che è ciò che il tema snow si aspetta per il bordo. Stesso markup, stessi\n      pixel, e montare Quill non sposta nulla. Dietro non c\'è ancora niente: solo HTML. }}\n  <div\n    class=\'bb-editor bb-editor-idle\'\n    {{on \'mousedown\' this.activate}}\n    {{on \'touchstart\' this.activate}}\n  >\n    {{this.idleToolbar}}\n    <div\n      class=\'ql-container ql-snow\'\n      role=\'textbox\'\n      aria-multiline=\'true\'\n      aria-label={{@ariaLabel}}\n      tabindex=\'0\'\n      {{on \'focus\' this.activate}}\n    >\n      <div class=\'ql-editor {{if this.isBlank \"ql-blank\"}}\'>{{this.idleContent}}</div>\n    </div>\n  </div>\n{{/if}}\n\n{{#unless @hideExtraTools}}\n  <span class=\'open-cf-canned-response\'>\n    <a {{on \'click\' this.toggleEditCode}}>\n      &lt; code &gt;\n    </a>\n    {{#if @applicationController}}\n      |\n      <a {{on \'click\' this.setCannedCustomField}}>\n        {{t \'predefined texts\'}}\n      </a>\n    {{/if}}\n  </span>\n{{/unless}}\n");

var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
const EMPTY_PARAGRAPH = htmlSafe('<p><br></p>');
let CfAdvancedTextareaComponent = (_dec = inject('quill'), _class = class CfAdvancedTextareaComponent extends Component {
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "quillService", _descriptor, this);
    // ─── Stato locale ─────────────────────────────────────────────────────────
    _initializerDefineProperty(this, "editCode", _descriptor2, this);
    // Montare Quill costa ~22ms di main thread a istanza. Una scheda clinica ne contiene
    // fino a 19 (template Nutrizionista: 19 advancedTextarea su 33 campi): montarli tutti
    // al render blocca il thread ~400ms, quasi sempre per editor che nessuno tocca.
    // Finché il campo non serve mostriamo la stessa cosa senza Quill dietro: una copia
    // della toolbar vera (29ms per 19, contro 433ms) sopra il testo. Identico a vedersi,
    // perché è lo stesso markup.
    _initializerDefineProperty(this, "mounted", _descriptor3, this);
    // Punto premuto dall'utente, se ha premuto: BbEditor lo usa per mettere il caret dove
    // ha cliccato. Il box fermo è grande quanto l'editor, quindi il punto resta valido.
    _initializerDefineProperty(this, "activation", _descriptor4, this);
    // Copia locale del valore per il binding della <Textarea> in modalità codice
    _initializerDefineProperty(this, "_localValue", _descriptor5, this);
    this._localValue = args.value ?? '';

    // La copia della toolbar deve venire da una toolbar vera, quindi qualcuno la deve
    // pur montare: il primo campo della pagina si offre. È un campo funzionante come
    // gli altri, non uno spreco, e da lì in poi tutti gli altri copiano lui.
    if (this.quillService.claimToolbarSource()) {
      this.mounted = true;
    }
  }

  // La toolbar di riposo compare appena il primo editor l'ha catturata (tracked): sui
  // campi già a schermo è il frame successivo.
  get idleToolbar() {
    return this.quillService.toolbarMarkup;
  }
  get isBlank() {
    return !this.args.value || !this.args.value.trim();
  }
  get idleContent() {
    return this.isBlank ? EMPTY_PARAGRAPH : sanitizeHtml(this.args.value);
  }

  // ─── ID univoco per MyQuill ───────────────────────────────────────────────

  get elementId() {
    return guidFor(this);
  }

  // ─── Sync quando il parent aggiorna @value esternamente ───────────────────

  syncValue() {
    this._localValue = this.args.value ?? '';
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  onTextareaInput(event) {
    this._localValue = event.target.value;
    this.args.onValueChange?.(this._localValue);
  }

  // Monta Quill quando il campo viene premuto, e lascia che sia BbEditor a mettere focus
  // e caret sul punto premuto. Un tentativo di montare prima, al passaggio del mouse, è
  // stato scartato: Ember renderizza fra l'arrivo del puntatore e la pressione, quindi
  // l'editor nasceva senza sapere di dover prendere il focus e serviva un secondo click.
  //
  // preventDefault è ciò che rende sufficiente un click solo: altrimenti il default di
  // mousedown dà il focus al box fermo, che un istante dopo non esiste più, e il focus
  // ricade sul body cancellando quello appena preso dall'editor.
  activate(event) {
    event.preventDefault();
    const touch = event.touches?.[0];
    this.activation = {
      x: event.clientX ?? touch?.clientX ?? null,
      y: event.clientY ?? touch?.clientY ?? null,
      focus: true
    };
    this.mounted = true;
  }
  toggleEditCode(e) {
    e.preventDefault();
    // Sync _localValue from @value before switching to code mode
    // so the textarea shows the latest HTML (MyQuill may have updated @value externally)
    if (!this.editCode) {
      this._localValue = this.args.value ?? '';
    }
    this.editCode = !this.editCode;
  }

  // Imposta il canned response sull'applicationController (se passato)
  setCannedCustomField(e) {
    e.preventDefault();
    if (this.args.applicationController && this.args.custom_field) {
      set(this.args.applicationController, 'cannedCustomField', this.args.custom_field);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "quillService", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "editCode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "mounted", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "activation", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "_localValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "syncValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onTextareaInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onTextareaInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "activate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "activate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleEditCode", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleEditCode"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setCannedCustomField", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setCannedCustomField"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, CfAdvancedTextareaComponent);

export { CfAdvancedTextareaComponent as default };
//# sourceMappingURL=cf-advanced-textarea.js.map
